import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { SERVER_URL } from "../../ServerLink";
import { formatCurrency, statusStyle } from "./Components/Api";
import { Previewer } from "../../../components/DocumentPreviewer/Previewer";
import { getFileExtension, imageTypes, pdfTypes, officeTypes, videoTypes }
  from "../../../components/DocumentPreviewer/utils";

const getFileType = (keyOrName) => {
  const ext = getFileExtension(keyOrName).toLowerCase();
  if (pdfTypes.includes(ext)) return "pdf";
  if (imageTypes.includes(ext)) return "image";
  if (videoTypes.includes(ext)) return "video";
  if (officeTypes.includes(ext)) return "office";
  return "unknown";
};

const fmtDate = (d) => (d ? moment(d).format("MMM D, YYYY") : "—");

// "204" becomes "Unit 204"; a value already prefixed, or a description typed
// into the unit field before the split ("Lawn care"), is left as typed.
// Mirrors Server/src/controllers/invoice/invoice.unit-label.ts.
const unitLabel = (raw) => {
  const u = String(raw || "").trim();
  if (!u) return "";
  if (/^unit\b/i.test(u)) return u;
  return /^#?(?=[A-Za-z-]*\d)[A-Za-z0-9-]{1,10}$/.test(u) ? `Unit ${u}` : u;
};

// Where one line item was carried out, as one line of text.
const itemPlace = (it) =>
  [
    it.propertyName,
    it.buildingName && it.buildingName !== it.propertyName ? it.buildingName : "",
    unitLabel(it.unitNo),
    String(it.jobDescription || "").trim(),
  ]
    .filter(Boolean)
    .join(" · ");

const Field = ({ label, value }) => (
  <div style={{ flex: "1 1 160px", minWidth: 140 }}>
    <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.6px" }}>
      {label}
    </div>
    <div style={{ fontSize: "0.92rem", color: "#0F172A", fontWeight: 600, marginTop: 3 }}>
      {value || "—"}
    </div>
  </div>
);

const InvoicePublicPreview = () => {
  const { id, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(
          `${SERVER_URL}/v2/invoices/invoice/public/${id}/${token}`
        );
        if (!mounted) return;
        const data = res?.data?.data;
        setInvoice(data);
        setActive((data?.attachments || []).find((a) => a.url) || null);
      } catch (e) {
        if (!mounted) return;
        setError(
          e?.response?.data?.message ||
          "This invoice preview link is no longer available."
        );
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id, token]);

  const cfg = invoice ? statusStyle(invoice.status) : null;
  // One invoice is several line items sharing one number, so the headline figure
  // is the invoice's payable total — `amount` is only the pre-GST subtotal.
  const payable = invoice ? (invoice.total != null ? invoice.total : invoice.amount) : 0;
  const items = (invoice?.items || []).filter(Boolean);
  const places = invoice?.properties || [];

  return (
    <div style={{ minHeight: "100vh", background: "#EEF2F7", padding: "32px 16px" }}>
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <img
            src="https://rentdigi-listing-us-east.s3.us-east-1.amazonaws.com/email-templates/logo/rdcare-logo.png"
            alt="Rentdigicare"
            style={{ height: 30 }}
          />
        </div>

        {loading ? (
          <div style={{ background: "#fff", borderRadius: 16, padding: "60px 0", textAlign: "center", border: "1px solid #D8E0EA" }}>
            <Spinner style={{ color: "#4F46E5" }} />
          </div>
        ) : error ? (
          <div style={{ background: "#fff", borderRadius: 16, padding: "48px 24px", textAlign: "center", border: "1px solid #D8E0EA" }}>
            <FeatherIcon icon="alert-triangle" size={34} color="#DC2626" />
            <div style={{ marginTop: 12, fontSize: "1rem", fontWeight: 700, color: "#0F172A" }}>
              Preview unavailable
            </div>
            <div style={{ marginTop: 6, fontSize: "0.86rem", color: "#64748B" }}>{error}</div>
          </div>
        ) : invoice ? (
          <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", border: "1px solid #D8E0EA" }}>
            <div style={{ height: 6, background: "#4338CA" }} />
            <div style={{ background: "#4338CA", padding: "28px 32px" }}>
              <div style={{ color: "#C7D2FE", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.2px" }}>
                Invoice {invoice.invoiceNoLabel || (invoice.invoiceNo ? `#${invoice.invoiceNo}` : "")}
              </div>
              <h1 style={{ color: "#fff", fontSize: "1.5rem", fontWeight: 600, margin: "8px 0 0" }}>
                {invoice.title || "Invoice"}
              </h1>
              <div style={{ color: "#E0E7FF", fontSize: "0.84rem", marginTop: 8 }}>
                From Vendor {invoice.vendorName || "Vendor"}
                {/* One invoice can span properties; naming only the first would
                    quietly hide the rest, so past one it says how many. */}
                {places.length > 1 ? (
                  ` · ${places.length} properties`
                ) : (
                  <>
                    {invoice.propertyName ? ` · ${invoice.propertyName}` : ""}
                    {invoice.buildingName ? ` — ${invoice.buildingName}` : ""}
                  </>
                )}
              </div>
            </div>

            <div style={{ padding: "28px 32px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 22 }}>
                <div style={{ fontSize: "1.7rem", fontWeight: 800, color: "#0F172A" }}>
                  {formatCurrency(payable, invoice.currency)}
                </div>
                {items.length > 1 && (
                  <span style={{ fontSize: "0.82rem", color: "#64748B" }}>
                    {items.length} items
                  </span>
                )}
                {cfg && (
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                    borderRadius: 20, padding: "4px 14px", fontSize: "0.8rem", fontWeight: 700,
                  }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.color }} />
                    {invoice.status}
                  </span>
                )}
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 18, paddingBottom: 22, borderBottom: "1px solid #E2E8F0" }}>
                <Field label="Vendor" value={invoice.vendorName} />
                <Field label="Category" value={invoice.categoryName} />
                {Array.isArray(invoice.properties) && invoice.properties.length > 1 ? (
                  <Field
                    label="Properties"
                    value={invoice.properties
                      .map((p) => [p.propertyName, p.buildingName].filter(Boolean).join(" — "))
                      .join(", ")}
                  />
                ) : (
                  <>
                    <Field label="Property" value={invoice.propertyName} />
                    <Field label="Building" value={invoice.buildingName} />
                  </>
                )}
                <Field label="Invoice Date" value={fmtDate(invoice.invoiceDate)} />
                <Field label="Due Date" value={fmtDate(invoice.dueDate)} />
                {invoice.referenceNo && <Field label="Invoice No." value={invoice.referenceNo} />}
              </div>

              {/* An invoice is several line items under one number. Listing them
                  is what makes the total above a figure the recipient can check
                  rather than one they have to take on trust. */}
              {items.length > 1 && (
                <div style={{ paddingTop: 22 }}>
                  <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 10 }}>
                    Items ({items.length})
                  </div>
                  <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
                    {items.map((it, i) => {
                      const place = itemPlace(it);
                      return (
                        <div
                          key={it.ordinal ?? i}
                          style={{
                            display: "flex", alignItems: "flex-start", gap: 12,
                            padding: "12px 16px",
                            borderTop: i ? "1px solid #E2E8F0" : "none",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#4338CA", letterSpacing: "0.4px" }}>
                              {it.label}
                            </div>
                            <div style={{ fontSize: "0.9rem", color: "#0F172A", fontWeight: 600, marginTop: 3 }}>
                              {it.title || "Service"}
                            </div>
                            {place && (
                              <div style={{ fontSize: "0.8rem", color: "#64748B", marginTop: 2 }}>{place}</div>
                            )}
                            {it.comment && (
                              <div style={{ fontSize: "0.8rem", color: "#64748B", marginTop: 2 }}>{it.comment}</div>
                            )}
                          </div>
                          <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                            <div style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A" }}>
                              {formatCurrency(it.total, invoice.currency)}
                            </div>
                            {/* Only a legacy invoice can hold several statuses at
                                once. When it does the header says "Mixed", and
                                this is what makes that answerable. */}
                            {invoice.status === "Mixed" && it.status && (
                              <div style={{ fontSize: "0.72rem", color: statusStyle(it.status).color, fontWeight: 700, marginTop: 3 }}>
                                {it.status}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ background: "#F8FAFC", borderTop: "1px solid #E2E8F0", padding: "12px 16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", color: "#64748B" }}>
                        <span>Subtotal</span>
                        <span style={{ color: "#0F172A", fontWeight: 600 }}>
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", color: "#64748B", marginTop: 4 }}>
                        <span>GST</span>
                        <span style={{ color: "#0F172A", fontWeight: 600 }}>
                          {formatCurrency(invoice.gstAmount, invoice.currency)}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.88rem", fontWeight: 700, color: "#0F172A", marginTop: 8, paddingTop: 8, borderTop: "1px solid #E2E8F0" }}>
                        <span>Total</span>
                        <span>{formatCurrency(payable, invoice.currency)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {invoice.description && (
                <div style={{ paddingTop: 18 }}>
                  <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 6 }}>
                    Description
                  </div>
                  <div style={{ fontSize: "0.9rem", color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                    {invoice.description}
                  </div>
                </div>
              )}

              {(invoice.attachments || []).length > 0 && (
                <div style={{ paddingTop: 24 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#0F172A", marginBottom: 10 }}>
                    <FeatherIcon icon="paperclip" size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
                    Attachments ({invoice.attachments.length})
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                    {invoice.attachments.map((a) => {
                      const isActive = active && active.key === a.key;
                      return (
                        <button
                          key={a.key}
                          type="button"
                          onClick={() => a.url && setActive(a)}
                          title={a.name}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            background: isActive ? "#EEF2FF" : "#F8FAFC",
                            border: `1px solid ${isActive ? "#A5B4FC" : "#E2E8F0"}`,
                            borderRadius: 8, padding: "7px 12px", cursor: a.url ? "pointer" : "not-allowed",
                            fontSize: "0.8rem", color: "#0F172A", maxWidth: 220,
                          }}
                        >
                          <FeatherIcon
                            icon={getFileType(a.name) === "image" ? "image" : getFileType(a.name) === "pdf" ? "file-text" : "file"}
                            size={13}
                            color="#4F46E5"
                          />
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
                        </button>
                      );
                    })}
                  </div>

                  {active && active.url && (
                    <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden", background: "#0F172A" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px" }}>
                        <span style={{ color: "#fff", fontSize: "0.82rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {active.name}
                        </span>
                        <a
                          href={active.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={active.name}
                          style={{ color: "#A5B4FC", fontSize: "0.8rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                        >
                          <FeatherIcon icon="download" size={13} /> Download
                        </a>
                      </div>
                      <Previewer signedUrl={active.url} type={getFileType(active.name)} height="64vh" width="100%" />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ background: "#F8FAFC", padding: "18px 32px", borderTop: "1px solid #E2E8F0", textAlign: "center" }}>
              <span style={{ fontSize: "0.78rem", color: "#64748B" }}>
                Sent via <strong style={{ color: "#4338CA" }}>Rentdigicare</strong> · GSK Properties
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export { InvoicePublicPreview };
export default InvoicePublicPreview;
